// src/pages/setting/holidaymaster.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input,
    Alert,
    Spinner,
} from 'reactstrap';
import { Plus, Edit, Trash2, RefreshCw, Eye, CheckCircle, XCircle } from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from "../../Components/Common/TableContainer";
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

// Types
interface Holiday {
    id: number;
    holiday_name: string;
    holiday_date: string;
    description?: string;
    status: boolean;
    created_at: string;
    updated_at: string;
}

const HolidayMaster: React.FC = () => {
    // State
    const [holidays, setHolidays] = useState<Holiday[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showInactive, setShowInactive] = useState(false);

    // Modal states
    const [modal, setModal] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
    const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
    const [formData, setFormData] = useState({
        holiday_name: '',
        holiday_date: '',
        description: '',
        status: true,
    });
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Delete modal
    const [deleteModal, setDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [holidayToDelete, setHolidayToDelete] = useState<Holiday | null>(null);

    // View modal
    const [viewModal, setViewModal] = useState(false);
    const [viewingHoliday, setViewingHoliday] = useState<Holiday | null>(null);

    // Fetch holidays
    const fetchHolidaysData = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiClient.get(`/holidays?include_inactive=${showInactive}`);
            console.log('API Response:', response); // Debugging line
            const data = response.data as Holiday[];
            setHolidays(data || []);
        } catch (error: any) {
            console.error('Error fetching holidays:', error);
            setError(error.message || 'Failed to fetch holidays');
            toast.error('Failed to load holidays');
        } finally {
            setLoading(false);
        }
    }, [showInactive]);

    // Initial fetch
    useEffect(() => {
        fetchHolidaysData();
    }, [fetchHolidaysData]);

    // Handle form input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    // Open add modal
    const openAddModal = () => {
        setModalMode('add');
        setFormData({
            holiday_name: '',
            holiday_date: '',
            description: '',
            status: true,
        });
        setFormError('');
        setModal(true);
    };

    // Format date for HTML input element (yyyy-MM-dd)
    const formatDateForInput = (dateString: string) => {
        try {
            return format(new Date(dateString), 'yyyy-MM-dd');
        } catch {
            return '';
        }
    };

    // Open edit modal
    const openEditModal = (holiday: Holiday) => {
        setModalMode('edit');
        setSelectedHoliday(holiday);
        setFormData({
            holiday_name: holiday.holiday_name,
            holiday_date: formatDateForInput(holiday.holiday_date),
            description: holiday.description || '',
            status: holiday.status,
        });
        setFormError('');
        setModal(true);
    };

    // Close modal
    const closeModal = () => {
        setModal(false);
        setSelectedHoliday(null);
        setFormError('');
    };

    // Handle form submit
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate
        if (!formData.holiday_name.trim()) {
            setFormError('Holiday name is required');
            return;
        }
        if (!formData.holiday_date) {
            setFormError('Holiday date is required');
            return;
        }

        setSubmitting(true);
        setFormError('');

        try {
            if (modalMode === 'add') {
                await apiClient.post('/holidays', {
                    holiday_name: formData.holiday_name.trim(),
                    holiday_date: formData.holiday_date,
                    description: formData.description.trim() || undefined,
                });
                toast.success('Holiday created successfully!');
            } else {
                await apiClient.put(`/holidays/${selectedHoliday?.id}`, {
                    holiday_name: formData.holiday_name.trim(),
                    holiday_date: formData.holiday_date,
                    description: formData.description.trim() || undefined,
                    status: formData.status,
                });
                toast.success('Holiday updated successfully!');
            }

            closeModal();
            fetchHolidaysData();
        } catch (error: any) {
            console.error('Error saving holiday:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to save holiday';
            setFormError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const openDeleteModal = (holiday: Holiday) => {
        setHolidayToDelete(holiday);
        setDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!holidayToDelete) return;

        setDeleting(true);
        try {
            await apiClient.delete(`/holidays/${holidayToDelete.id}`);
            toast.success('Holiday deleted successfully!');
            setDeleteModal(false);
            fetchHolidaysData();
        } catch (error: any) {
            console.error('Error deleting holiday:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to delete holiday';
            toast.error(msg);
        } finally {
            setDeleting(false);
            setHolidayToDelete(null);
        }
    };

    // Handle restore
    const handleRestore = async (holiday: Holiday) => {
        try {
            await apiClient.patch(`/holidays/${holiday.id}/restore`);
            toast.success('Holiday restored successfully!');
            fetchHolidaysData();
        } catch (error: any) {
            console.error('Error restoring holiday:', error);
            const msg = error.response?.data?.message || error.message || 'Failed to restore holiday';
            toast.error(msg);
        }
    };

    // Handle view
    const handleView = (holiday: Holiday) => {
        setViewingHoliday(holiday);
        setViewModal(true);
    };

    // Format date
    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch {
            return 'N/A';
        }
    };

    // Get status badge
    const getStatusBadge = (status: boolean) => {
        return status ? (
            <Badge color="success">
                <CheckCircle size={12} className="me-1" />
                Active
            </Badge>
        ) : (
            <Badge color="warning">
                <XCircle size={12} className="me-1" />
                Inactive
            </Badge>
        );
    };

    // Table columns
    const columns = useMemo(
        () => [
            {
                header: 'SI.No',
                accessorKey: 'id',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    return <strong>{cell.row.index + 1}</strong>;
                },
            },
            {
                header: 'Holiday Name',
                accessorKey: 'holiday_name',
                cell: (cell: any) => (
                    <span className="fw-semibold">{cell.getValue()}</span>
                ),
            },
            {
                header: 'Holiday Date',
                accessorKey: 'holiday_date',
                cell: (cell: any) => formatDate(cell.getValue()),
            },
            {
                header: 'Description',
                accessorKey: 'description',
                cell: (cell: any) => cell.getValue() || '—',
            },
            {
                header: 'Status',
                accessorKey: 'status',
                cell: (cell: any) => getStatusBadge(cell.getValue()),
            },
            {
                header: 'Actions',
                accessorKey: 'actions',
                enableColumnFilter: false,
                enableSorting: false,
                cell: (cell: any) => {
                    const holiday = cell.row.original;
                    return (
                        <div className="d-flex gap-1">
                            <Button
                                color="info"
                                size="sm"
                                title="View"
                                onClick={() => handleView(holiday)}
                            >
                                <Eye size={14} />
                            </Button>
                            <Button
                                color="primary"
                                size="sm"
                                title="Edit"
                                onClick={() => openEditModal(holiday)}
                            >
                                <Edit size={14} />
                            </Button>
                            {holiday.status ? (
                                <Button
                                    color="danger"
                                    size="sm"
                                    title="Delete"
                                    onClick={() => openDeleteModal(holiday)}
                                >
                                    <Trash2 size={14} />
                                </Button>
                            ) : (
                                <Button
                                    color="success"
                                    size="sm"
                                    title="Restore"
                                    onClick={() => handleRestore(holiday)}
                                >
                                    <RefreshCw size={14} />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ],
        []
    );

    return (
        <div className="page-content">
            <Container fluid>
                <BreadCrumb title="Holiday Settings" pageTitle="Holidays" />

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <h5 className="card-title mb-0">Holiday Settings</h5>
                                <div className="d-flex gap-2">
                                    <FormGroup check className="mb-0 d-flex align-items-center">
                                        <Label check className="me-2 mb-0">
                                            <Input
                                                type="checkbox"
                                                checked={showInactive}
                                                onChange={() => setShowInactive(!showInactive)}
                                            />
                                            <span className="ms-1">Show Inactive</span>
                                        </Label>
                                    </FormGroup>
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => fetchHolidaysData()}
                                        disabled={loading}
                                    >
                                        <RefreshCw size={14} className="me-1" />
                                        Refresh
                                    </Button>
                                    <Button
                                        color="primary"
                                        size="sm"
                                        onClick={openAddModal}
                                    >
                                        <Plus size={14} className="me-1" />
                                        Add Holiday
                                    </Button>
                                </div>
                            </CardHeader>

                            <CardBody>
                                {error && (
                                    <Alert color="danger" className="mb-3">
                                        <i className="ri-error-warning-line align-middle me-2"></i>
                                        {error}
                                    </Alert>
                                )}

                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner color="primary" className="me-2" />
                                        <span>Loading holidays...</span>
                                    </div>
                                ) : holidays.length === 0 ? (
                                    <div className="text-center py-5">
                                        <i className="ri-calendar-event-line display-4 text-muted"></i>
                                        <h5 className="mt-3">No holidays found</h5>
                                        <p className="text-muted">
                                            {showInactive
                                                ? "No holidays match your filter criteria."
                                                : "Click 'Add Holiday' to create your first holiday."}
                                        </p>
                                    </div>
                                ) : (
                                    <TableContainer
                                        columns={columns}
                                        data={holidays}
                                        customPageSize={10}
                                        tableClass="table-striped"
                                        theadClass="table-light"
                                    />
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Add / Edit Modal */}
            <Modal isOpen={modal} toggle={closeModal} centered>
                <ModalHeader toggle={closeModal}>
                    {modalMode === 'add' ? 'Add Holiday' : 'Edit Holiday'}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        {formError && (
                            <Alert color="danger">
                                {formError}
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="holiday_name">Holiday Name <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                name="holiday_name"
                                id="holiday_name"
                                placeholder="Enter holiday name"
                                value={formData.holiday_name}
                                onChange={handleInputChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="holiday_date">Holiday Date <span className="text-danger">*</span></Label>
                            <Input
                                type="date"
                                name="holiday_date"
                                id="holiday_date"
                                value={formData.holiday_date}
                                onChange={handleInputChange}
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="description">Description</Label>
                            <Input
                                type="textarea"
                                name="description"
                                id="description"
                                placeholder="Enter description"
                                value={formData.description}
                                onChange={handleInputChange}
                                rows="3"
                            />
                        </FormGroup>
                        {modalMode === 'edit' && (
                            <FormGroup check className="mt-3">
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        name="status"
                                        checked={formData.status}
                                        onChange={handleInputChange}
                                    />{' '}
                                    Active
                                </Label>
                            </FormGroup>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={closeModal} disabled={submitting}>
                            Cancel
                        </Button>
                        <Button color="primary" type="submit" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner size="sm" className="me-1" />
                                    Saving...
                                </>
                            ) : (
                                'Save'
                            )}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* View Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)} centered>
                <ModalHeader toggle={() => setViewModal(false)}>
                    Holiday Details
                </ModalHeader>
                <ModalBody>
                    {viewingHoliday && (
                        <div>
                            <Row className="mb-2">
                                <Col xs={4} className="fw-semibold">Holiday Name:</Col>
                                <Col xs={8}>{viewingHoliday.holiday_name}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="fw-semibold">Holiday Date:</Col>
                                <Col xs={8}>{formatDate(viewingHoliday.holiday_date)}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="fw-semibold">Description:</Col>
                                <Col xs={8}>{viewingHoliday.description || '—'}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="fw-semibold">Status:</Col>
                                <Col xs={8}>{getStatusBadge(viewingHoliday.status)}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="fw-semibold">Created At:</Col>
                                <Col xs={8}>{formatDate(viewingHoliday.created_at)}</Col>
                            </Row>
                            <Row className="mb-2">
                                <Col xs={4} className="fw-semibold">Updated At:</Col>
                                <Col xs={8}>{formatDate(viewingHoliday.updated_at)}</Col>
                            </Row>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setViewModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
                <ModalHeader toggle={() => setDeleteModal(false)}>
                    Confirm Delete
                </ModalHeader>
                <ModalBody>
                    <p>
                        Are you sure you want to delete the holiday{' '}
                        <strong>"{holidayToDelete?.holiday_name}"</strong>?
                    </p>
                    <Alert color="warning" className="mb-0">
                        <i className="ri-alert-line me-2"></i>
                        This action will deactivate the holiday. You can restore it later.
                    </Alert>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={() => setDeleteModal(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        color="danger"
                        onClick={confirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            'Delete'
                        )}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default HolidayMaster;
